import { nextStepLabel } from "./format.ts";
import type { FollowUp, RealEstateCase, TimelineActivity } from "./types.ts";

export type WaitingOutcome = "Má záujem" | "Ozvať sa neskôr" | "Stále odmieta" | "Definitívne vyradiť";

const createId = (prefix: string) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const addDays = (value: string, days: number) => {
  const date = new Date(value);
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
};

const activity = (title: string, body: string, createdAt: string, followUp?: FollowUp): TimelineActivity => ({
  id: createId("activity"),
  createdAt,
  type: "stage",
  title,
  body,
  followUp
});

export const waitingFollowUp = (
  anchor: string,
  days: number,
  preferredTime = "09:00",
  purpose = "Overiť situáciu klienta"
): FollowUp => ({ date: addDays(anchor, days), time: preferredTime || "09:00", purpose });

export const moveCaseToWaiting = (caseItem: RealEstateCase, changedAt: string, days = 30): RealEstateCase => {
  const followUp = waitingFollowUp(changedAt, days, caseItem.followUp?.time ?? "09:00");
  return {
    ...caseItem,
    leadState: "Čakajúci",
    lastBusinessStage: caseItem.stage,
    waitingSince: changedAt,
    stage: "Čakajúci",
    status: "Čakajúci",
    followUp,
    nextStep: `Zavolať klientovi - ${nextStepLabel(followUp)}`,
    timeline: [activity(
      "Prípad presunutý medzi čakajúce",
      `Klient momentálne nemá záujem. Ďalší kontakt: ${nextStepLabel(followUp)}.`,
      changedAt,
      followUp
    ), ...caseItem.timeline],
    updatedAt: changedAt
  };
};

export const applyWaitingOutcome = (
  caseItem: RealEstateCase,
  outcome: Exclude<WaitingOutcome, "Definitívne vyradiť">,
  changedAt: string,
  followUp: FollowUp
): RealEstateCase => {
  const active = outcome === "Má záujem";
  return {
    ...caseItem,
    leadState: active ? "Aktívny" : "Čakajúci",
    stage: active ? "Aktívne" : "Čakajúci",
    status: active ? "Aktívne" : "Čakajúci",
    waitingSince: active ? undefined : caseItem.waitingSince ?? changedAt,
    followUp,
    nextStep: active ? nextStepLabel(followUp) : `Zavolať klientovi - ${nextStepLabel(followUp)}`,
    timeline: [activity(
      active ? "Čakajúci lead je opäť aktívny" : `Výsledok čakajúceho follow-upu: ${outcome}`,
      `Výsledok: ${outcome}. Ďalší krok: ${nextStepLabel(followUp)}.`,
      changedAt,
      followUp
    ), ...caseItem.timeline],
    updatedAt: changedAt
  };
};

export const excludeCase = (caseItem: RealEstateCase, reason: string, changedAt: string): RealEstateCase => ({
  ...caseItem,
  leadState: "Vyradený",
  lastBusinessStage: caseItem.lastBusinessStage ?? caseItem.stage,
  stage: "Vyradený",
  status: "Vyradený",
  excludedAt: changedAt,
  exclusionReason: reason,
  lostReason: reason,
  closedLostAt: changedAt,
  contactCountAtExclusion: caseItem.timeline.filter((item) => item.type === "call" || item.type === "follow-up").length,
  followUp: undefined,
  nextStep: "Bez ďalšieho kroku",
  timeline: [activity("Prípad definitívne vyradený", `Dôvod: ${reason}.`, changedAt), ...caseItem.timeline],
  updatedAt: changedAt
});
